import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Clock, MessageSquare, ArrowRight } from 'lucide-react'
import { useState } from 'react'

export default function Contact(){
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  })
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('submitting')

    // Simulate form submission (replace with actual API call)
    setTimeout(() => {
      setStatus('success')
      setFormData({ name: '', email: '', phone: '', company: '', message: '' })
    }, 1500)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Us',
      value: 'support@adkarrot.com',
      description: 'We typically respond within 4 hours during business hours',
      link: 'mailto:support@adkarrot.com'
    },
    {
      icon: Phone,
      title: 'Call Us',
      value: '(319) 343-6094',
      description: 'Monday - Friday, 9am - 5pm CST',
      link: 'tel:+13193436094'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      value: '309 Court Avenue Suite 855',
      description: 'Des Moines, IA 50309',
      link: 'https://maps.google.com/?q=309+Court+Avenue+Suite+855+Des+Moines+IA+50309'
    }
  ]

  return (
    <>
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white via-cloud-50/30 to-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block mb-6 px-5 py-2.5 bg-gradient-to-r from-carrot-50 to-carrot-100/50 text-carrot-700 rounded-full text-sm font-semibold tracking-wide shadow-subtle">
              Get In Touch
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-ink-900 mb-8 leading-[1.1]">
              Let's Talk Growth
            </h1>
            <p className="text-xl md:text-2xl text-ink-600 font-light leading-relaxed">
              Have questions? Ready to start growing? We're here to help and typically respond within one business day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-20">
            {contactMethods.map((method, idx) => {
              const Icon = method.icon
              return (
                <motion.a
                  key={idx}
                  href={method.link}
                  target={method.link.startsWith('http') ? '_blank' : undefined}
                  rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="group bg-white rounded-3xl border border-ink-100 p-8 shadow-card hover:shadow-soft transition-all duration-500 ease-apple hover:-translate-y-1 cursor-pointer"
                >
                  <div className="w-16 h-16 rounded-2xl bg-carrot-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 ease-apple">
                    <Icon className="w-8 h-8 text-carrot-600" />
                  </div>
                  <h3 className="text-xl font-bold text-ink-900 mb-2">{method.title}</h3>
                  <p className="text-lg text-carrot-600 font-semibold mb-2">{method.value}</p>
                  <p className="text-sm text-ink-600">{method.description}</p>
                </motion.a>
              )
            })}
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="bg-gradient-to-br from-cloud-50 to-white rounded-[3rem] p-12 md:p-16 border border-ink-100 shadow-soft"
            >
              <div className="flex items-center gap-3 mb-8">
                <MessageSquare className="w-8 h-8 text-carrot-600" />
                <h2 className="text-3xl font-bold text-ink-900">Send Us a Message</h2>
              </div>

              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 rounded-full bg-leaf-100 flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-leaf-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-ink-900 mb-3">Message Sent!</h3>
                  <p className="text-ink-600 mb-6">
                    Thanks for reaching out! We'll get back to you within one business day.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="text-carrot-600 hover:text-carrot-700 font-semibold"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-ink-900 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-2xl border border-ink-200 focus:border-carrot-500 focus:ring-2 focus:ring-carrot-200 transition-all duration-200 outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-ink-900 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-2xl border border-ink-200 focus:border-carrot-500 focus:ring-2 focus:ring-carrot-200 transition-all duration-200 outline-none"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-ink-900 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-2xl border border-ink-200 focus:border-carrot-500 focus:ring-2 focus:ring-carrot-200 transition-all duration-200 outline-none"
                        placeholder="(319) 555-0123"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-ink-900 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-2xl border border-ink-200 focus:border-carrot-500 focus:ring-2 focus:ring-carrot-200 transition-all duration-200 outline-none"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-ink-900 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-2xl border border-ink-200 focus:border-carrot-500 focus:ring-2 focus:ring-carrot-200 transition-all duration-200 outline-none resize-none"
                      placeholder="Tell us about your business and how we can help..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full group inline-flex items-center justify-center px-10 py-5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold text-lg rounded-2xl shadow-soft hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? 'Sending...' : 'Send Message'}
                    {status !== 'submitting' && (
                      <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
                    )}
                  </button>

                  <p className="text-sm text-ink-500 text-center">
                    By submitting this form, you agree to our{' '}
                    <Link to="/legal/privacy" className="text-carrot-600 hover:text-carrot-700">
                      Privacy Policy
                    </Link>
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Response Time Banner */}
      <section className="py-16 bg-gradient-to-r from-carrot-50 to-leaf-50">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center gap-3 mb-4"
            >
              <Clock className="w-8 h-8 text-carrot-600" />
              <h3 className="text-2xl font-bold text-ink-900">Fast Response Time</h3>
            </motion.div>
            <p className="text-lg text-ink-700">
              We pride ourselves on quick, helpful responses. Expect to hear back within <strong>1 business day</strong>,
              often much sooner. For urgent matters, give us a call at (319) 343-6094.
            </p>
          </div>
        </div>
      </section>

      {/* CTA to Pricing */}
      <section className="py-32 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-carrot-500 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-leaf-500 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
                Ready to get started?
              </h2>
              <p className="text-xl md:text-2xl text-cloud-200 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
                Skip the back-and-forth. Book a strategy call and let's create your custom growth roadmap today.
              </p>
              <Link
                to="/pricing"
                className="group inline-flex items-center px-10 py-5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold text-lg rounded-2xl shadow-glow hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02]"
              >
                View Pricing & Book Now
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
